<script lang="ts" setup>
  import { useQueryCache } from '@pinia/colada';
  import { ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { api } from '../../../../shared/api/client.ts';
  import { playerKeys } from '../../../../shared/api/player.ts';
  import { messageOf } from '../../../../shared/api/serverFailure.ts';
  import { route } from '../../../../shared/config/routes.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import { usePlayerSession } from '../../model/playerSession.ts';
  import RegistrationView from '../RegistrationView.vue';
  import RejoinGameCard from '../RejoinGameCard.vue';

  const props = defineProps<{ gameCode: string }>();

  const router = useRouter();
  const nickname = ref<string>('');
  const err = ref<string>('');
  const nicknameErr = ref<string>('');
  const isJoiningGame = ref(false);
  const { problem: sessionProblem, session, rejoinGame } = usePlayerSession({ asHost: false });
  const queryCache = useQueryCache();

  const joinGame = async () => {
    if (isJoiningGame.value) return;

    startUserAction(userAction.registrationJoinGame);
    isJoiningGame.value = true;
    try {
      const { error } = await api.POST('/games/{code}/players', {
        params: { path: { code: parseInt(props.gameCode) } },
        body: { nickname: nickname.value },
      });

      if (!error) {
        await queryCache.invalidateQueries({ key: playerKeys.session });
        await router.push({ name: route.game, params: { gameCode: props.gameCode }, query: { showRules: 'true' } });
        return;
      }

      if (error.slug === 'nickname-taken') nicknameErr.value = error.message;
      else err.value = messageOf('joinGame', error);
    } finally {
      isJoiningGame.value = false;
    }
  };

  watch(nickname, () => {
    err.value = '';
    nicknameErr.value = '';
  });
</script>

<template>
  <RegistrationView
    v-model="nickname"
    :error="err || sessionProblem"
    :game-code="gameCode"
    :hint="session && 'Enter a nickname below to join as a new player.'"
    :nickname-error="nicknameErr"
    :submitting="isJoiningGame"
    submit-text="Join game"
    submitting-text="Joining game…"
    @submit="joinGame"
  >
    <RejoinGameCard v-if="session" :session="session" @rejoin="rejoinGame" />
  </RegistrationView>
</template>
